/* eslint-disable no-sequences */
import { useState, useEffect, useRef, useCallback, useMemo, type PropsWithChildren } from 'react';
import { useRouter } from 'next/router';
import Headroom from 'react-headroom';
import dynamic from 'next/dynamic';

import { HeadSEO } from 'components/base/headSEO';
import { Breadcrumb } from 'components/breadcrumb';
import { useI18n } from 'context/i18n';
import { useAppData } from 'context/app';

import { getLang, getMarket, getCookie, COOKIES, setCookie } from 'utils/helpers';
import { dataLayerHandleEvent } from 'utils/dataLayers';

import { useLocale } from 'hooks';

import { SearchTNM } from 'components/search';
import { getDomainValue } from 'utils/domain';

import { Button } from 'components/ui/button';
import { useResizeObserver } from 'hooks/useResizeObserver';
import type { NewsletterCmsData } from 'components/newsletterModal';
import { SearchBoxJson } from 'components/ld-json/searchLdJson';
import { LabSearchIcon, LabSearchMobileIcon } from 'components/camperlab/icons/search';
import { LanguageSwitcher } from './language-switcher';
import { MyOrderModal } from './my-order-modal';
import { GiftBagModal } from 'components/layout/gift-bag-modal';
import { EventModal } from './event-modal';
import styles from './style.module.css';
import { TopScrollButton } from './top-scroll-button';
import { useFeature } from 'context/feature-flags';

const Footer = getDomainValue({
  camper: dynamic(() => import('@camper/react-web-components').then((res) => res.ORGANISMS.Footer)),
  nnormal: dynamic(() => import('@nnormal/react-web-components').then((res) => res.ORGANISMS.Footer)),
  camperlab: dynamic(() => import('@camper/react-web-components').then((res) => res.ORGANISMS.Footer)),
});
const TnmComponent = getDomainValue({
  camper: dynamic(() => import('@camper/react-web-components').then((res) => res.ORGANISMS.TnmComponent)),
  nnormal: dynamic(() => import('@nnormal/react-web-components').then((res) => res.ORGANISMS.TnmComponent)),
  camperlab: dynamic(() => import('@camper/react-web-components').then((res) => res.ORGANISMS.TnmComponent)),
});
const Ribbon = dynamic(() => import('components/layout/ribbon').then((mod) => mod.Ribbon));
const MiniBag = dynamic(() => import('components/miniBag').then((mod) => mod.MiniBag));
const MiniBagCamperlab = dynamic(() => import('components/miniBag/camperlab').then((mod) => mod.MiniBagCamperlab));
const AccountMenu = dynamic(() => import('components/layout/account-menu').then((mod) => mod.AccountMenu));
const MobileAccountButton = dynamic(() => import('components/layout/mobile-account-button').then((mod) => mod.MobileAccountButton));
const MembersOnlyModal = dynamic(() => import('components/membersOnlyModal').then((mod) => mod.MembersOnlyModal), { ssr: false });
const StoreDataRow = dynamic(() => import('components/storeDataRow').then((mod) => mod.StoreDataRow));
const AbandonedCartBanner = dynamic(() => import('components/abandonedCartBanner').then((mod) => mod.AbandonedCartBanner));
const NewsletterModal = dynamic(() => import('components/newsletterModal').then((mod) => mod.NewsletterModal));
const LabSearchDrawer = dynamic(() => import('components/search/lab-search-drawer').then((mod) => mod.LabSearchDrawer));
const MyAccountModal = dynamic(() => import('components/myAccount/my-account-modal').then((mod) => mod.MyAccountModal));
const ShoeCareAppointmentModal = dynamic(() => import('components/appointmentModal/shoe-care-appointment').then((mod) => mod.ShoeCareAppointment));
const FallbackTnm = dynamic(() => import('components/layout/fallback/tnm').then((mod) => mod.FallbackTNM));
const FallbackFooter = dynamic(() => import('components/layout/fallback/footer').then((mod) => mod.FallbackFooter));

const widgetBagHidden = ['/compra'];

function WithHeadRoom({ children, onPin, onUnpin }: { children: React.ReactNode; onPin: () => void; onUnpin: () => void }) {
  if (getDomainValue({ camper: false, nnormal: false, camperlab: true })) {
    return children;
  }
  return (
    <Headroom onPin={onPin} onUnpin={onUnpin}>
      {children}
    </Headroom>
  );
}

type LayoutProps = PropsWithChildren<{
  footerProps: any;
  withFooter?: boolean;
  tnmProps: any;
  breadcrumbs?: boolean;
  breadcrumbsBottom?: boolean;
  breadcrumbI18n?: any;
  breadcrumbFragments?: any[];
  ribbon?: any[];
  metatags?: { pageTitle?: string; metaDescription?: string } | null;
  camperlab?: boolean;
  scrollArrow?: boolean;
  idBag?: string;
  pageName?: string | null;
  translateSlug?: boolean;
  recycled?: boolean;
  gtmProps?: any;
  popupProps?: NewsletterCmsData;
}>;

export function Layout({
  children,
  footerProps,
  withFooter = true,
  tnmProps,
  breadcrumbs = true,
  breadcrumbsBottom = false,
  breadcrumbI18n = {},
  breadcrumbFragments = [],
  ribbon = [],
  metatags = null,
  camperlab = false,
  scrollArrow = false,
  pageName = null,
  translateSlug = false,
  recycled = false,
  gtmProps,
  popupProps,
}: LayoutProps) {
  const { pathname } = useRouter();

  const { t, profileData } = useI18n();
  const { newsletter } = useAppData();
  const isInFallback = useFeature('cms_fallback');
  const { locale } = useLocale();

  const { global_e, currency } = profileData;
  const [showingTnm, setShowingTnm] = useState(false);

  useEffect(() => {
    window.sendDataLayer = (eventPayload) => {
      dataLayerHandleEvent(eventPayload);
    };

    if (global_e) {
      const [lang, country] = [getLang(locale), getMarket(locale)];
      const data = { countryISO: country, currencyCode: currency, cultureCode: lang };

      const GlobalE_Data = encodeURIComponent(JSON.stringify(data));

      setCookie(COOKIES.GLOBAL_E, GlobalE_Data, 1, COOKIES.DOMAIN);
    }
  }, []);

  const handleOnPinNav = () => {
    const filterNav = document.querySelector<HTMLElement>('#plp-filter-nav');
    const isDesktop = !(window.innerWidth < 1200);
    const tnmSelector = `.tnm--${isDesktop ? 'desktop' : 'mobile'}`;
    const tnmElement = document.querySelector<HTMLElement>(tnmSelector);

    if (filterNav && tnmElement) {
      const tnmHeight = tnmElement.offsetHeight;
      filterNav.style.top = `${tnmHeight}px`;
      const filterPanel = document.querySelector<HTMLElement>('#plp-filter-panel');
      if (filterPanel && tnmHeight) {
        (filterPanel.firstChild as HTMLElement).style.top = `${filterNav.offsetHeight + tnmHeight}px`;
        (filterPanel.firstChild as HTMLElement).style.maxHeight = `calc(100vh - ${filterNav.offsetHeight + tnmHeight}px)`;
      }
    }
    const stickyCol = document.querySelector<HTMLElement>('#sticky-col');
    if (stickyCol && tnmElement) {
      const tnmHeight = tnmElement.offsetHeight;
      stickyCol.style.top = `${tnmHeight}px`;
    }
    const landingSticky = document.querySelector<HTMLElement>('#landing-sticky');
    if (landingSticky && tnmElement) {
      const tnmHeight = tnmElement.offsetHeight;
      landingSticky.style.top = `${tnmHeight}px`;
    }
  };
  const handleOnUnpinNav = () => {
    const filterNav = document.querySelector<HTMLElement>('#plp-filter-nav');
    if (filterNav) {
      filterNav.style.top = '0px';
      const filterPanel = document.querySelector<HTMLElement>('#plp-filter-panel');
      if (filterPanel) {
        (filterPanel.firstChild as HTMLElement).style.top = `${filterNav.offsetHeight}px`;
        (filterPanel.firstChild as HTMLElement).style.maxHeight = `calc(100vh - ${filterNav.offsetHeight}px)`;
      }
    }
    const stickyCol = document.querySelector<HTMLElement>('#sticky-col');
    if (stickyCol) {
      stickyCol.style.top = '0px';
    }
    const landingSticky = document.querySelector<HTMLElement>('#landing-sticky');
    if (landingSticky) {
      landingSticky.style.top = '0px';
    }
  };
  const onTnmShow = () => {
    if (window?.hideAutosuggest) {
      window.hideAutosuggest();
    }

    if (!showingTnm) {
      setShowingTnm(true);
    }
  };

  const onTnmHide = () => {
    setShowingTnm(false);
  };

  const isCamperlabInCamper = getDomainValue({ camper: camperlab, nnormal: false, camperlab: false });

  let layoutStyle = undefined;
  if (recycled) {
    layoutStyle = { '--background': 'var(--color-recycled)' };
  }

  const accountWidget = getDomainValue({
    camper: <MobileAccountButton onPress={() => window.openAccountModal?.()} />,
    nnormal: <MobileAccountButton onPress={() => window.openAccountModal?.()} />,
    camperlab: (
      <Button
        variant="none"
        className="flex items-center text-lg font-bold uppercase lg:text-xs [&_span]:!py-0"
        decoration="underline"
        onPress={() => window.openMyOrderModal?.('account')}
      >
        <span className="animated-underline inline-flex items-center whitespace-nowrap !py-0 after:hidden">{t('mi.cuenta', 'mis.pedidos', 'Orders')}</span>
      </Button>
    ),
  });

  const headerRef = useRef<HTMLElement | null>(null);

  // Callback para actualizar la altura del header cuando cambia su tamaño
  const handleResize = useCallback((entries: ResizeObserverEntry[]) => {
    const entry = entries[0];
    if (entry && entry.target instanceof HTMLElement) {
      const tnmHeight = entry.target.offsetHeight;
      document.body.style.setProperty('--tnm-height', `${tnmHeight}px`);
    }
  }, []);

  // Hook para observar cambios en el tamaño del elemento header
  const resizeObserverRef = useResizeObserver(handleResize);

  const handleHeaderHeight = useCallback(
    (node: HTMLElement | null) => {
      // get node height and set it as a style variable --tnm-height
      if (node) {
        headerRef.current = node;
        const tnmHeight = node.offsetHeight;

        document.body.style.setProperty('--tnm-height', `${tnmHeight}px`);

        // Configurar ResizeObserver para mantener actualizada la altura
        resizeObserverRef(node);
      }
    },
    [resizeObserverRef],
  );

  return (
    <div className={`${styles.layout}`} data-camperlab={isCamperlabInCamper ? 'true' : undefined} style={layoutStyle}>
      <StoreDataRow />
      <SearchBoxJson />
      <HeadSEO title={metatags?.pageTitle} description={metatags?.metaDescription} pageName={pageName} gtmProps={gtmProps} />
      <Ribbon
        items={ribbon}
        onClickNewsletter={() => {
          window.openNewsletterModal?.('header', { direct: true });
        }}
        accountWidget={<AccountMenu />}
        camperlab={getDomainValue({ camper: camperlab, nnormal: false, camperlab: true })}
      />

      <header ref={handleHeaderHeight} className={`${styles.headerNav} lab:min-h-none lab:sticky lab:top-0 lab:z-30 lab:bg-neutral`}>
        {tnmProps && (
          <WithHeadRoom onPin={handleOnPinNav} onUnpin={handleOnUnpinNav}>
            {isInFallback ?
              <FallbackTnm
                bag={
                  !widgetBagHidden.includes(pathname) ?
                    getDomainValue({
                      camper: <MiniBag isInSalePath={pathname === '/bolsa'} />,
                      nnormal: <MiniBag isInSalePath={pathname === '/bolsa'} />,
                      camperlab: <MiniBagCamperlab isInSalePath={pathname === '/bolsa'} />,
                    })
                  : undefined
                }
              />
            : <TnmComponent
                bagWidget={
                  !widgetBagHidden.includes(pathname) ?
                    getDomainValue({
                      camper: <MiniBag isInSalePath={pathname === '/bolsa'} />,
                      nnormal: <MiniBag isInSalePath={pathname === '/bolsa'} />,
                      camperlab: <MiniBagCamperlab isInSalePath={pathname === '/bolsa'} />,
                    })
                  : null
                }
                bagWidgetModal
                onTnmShow={onTnmShow}
                onTnmHide={onTnmHide}
                showSearch={true}
                searcher={getDomainValue({
                  camper: <SearchTNM />,
                  nnormal: <SearchTNM />,
                  camperlab: (
                    <Button
                      variant="none"
                      className="search-button flex items-center text-lg font-bold uppercase lg:text-xs [&_span]:!py-0"
                      onPress={() => window.openSearch?.()}
                    >
                      <LabSearchMobileIcon className="lg:hidden" />
                      <LabSearchIcon className="max-lg:hidden" />
                      <span className="search-button__text animated-underline py-0 after:hidden max-lg:sr-only lg:inline-flex lg:items-center">
                        {t('search.widget', 'search', 'Search')}
                      </span>
                    </Button>
                  ),
                })}
                {...tnmProps}
                accountWidget={accountWidget}
                showI18nModal={() => window.openLocaleSwitcher?.()}
                showNewsletterModal={() => {
                  window.openNewsletterModal?.('footer', { direct: true });
                }}
                showOrderTrackingModal={() => window.openMyOrderModal?.()}
                isStoreMode={Boolean(getCookie(COOKIES.STORE_MODE))}
              />
            }
            {getDomainValue({ camper: <AbandonedCartBanner /> })}
          </WithHeadRoom>
        )}
      </header>

      <div
        id="layoutContent"
        className={
          getDomainValue({ camper: showingTnm, nnormal: showingTnm, camperlab: false }) ?
            `${styles.backdrop} ${styles.backdropVisible}`
          : `${styles.backdrop} lab:mt-header`
        }
      >
        {breadcrumbs && !breadcrumbsBottom && <Breadcrumb i18nKeys={breadcrumbI18n} camperlab={camperlab} fragments={breadcrumbFragments} />}
        {children}
        {breadcrumbs && breadcrumbsBottom && (
          <div className={styles.breadcrumbsBottom}>
            <Breadcrumb i18nKeys={breadcrumbI18n} camperlab={camperlab} fragments={breadcrumbFragments} pageName={pageName} />
          </div>
        )}
        {withFooter ?
          <>
            {isInFallback ?
              <FallbackFooter />
            : footerProps ?
              <Footer
                {...footerProps}
                showI18nModal={() => window.openLocaleSwitcher?.()}
                showNewsletterModal={() => {
                  window.openNewsletterModal?.('footer', { direct: true });
                }}
                profileData={profileData}
                showOrderTrackingModal={() => {
                  window?.openMyOrderModal?.('tracking');
                }}
                newsletterTitle={newsletter?.title ?? ''}
                newsletterDescription={newsletter?.text ?? ''}
              />
            : null}
          </>
        : null}
      </div>
      <MyAccountModal />
      <GiftBagModal />
      <LanguageSwitcher translateSlug={translateSlug} />
      <EventModal />
      <NewsletterModal cmsData={popupProps} camperlab={camperlab} />
      <MembersOnlyModal camperlab={camperlab} />
      <ShoeCareAppointmentModal />
      <MyOrderModal />
      {getDomainValue({ camperlab: <LabSearchDrawer /> })}
      {getDomainValue({ camper: scrollArrow, nnormal: scrollArrow, camperlab: false }) ?
        <TopScrollButton />
      : null}
    </div>
  );
}

export default Layout;
